import "./Category.scss";

import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiEdit2 } from "react-icons/fi";

import DeleteButton from "../../../components/DeleteButton/DeleteButton";
import Button from "../../../components/Utils/Button/Button";
import ButtonSwitcher from "../../../components/Utils/ButtonSwitcher/ButtonSwitcher";
import DynamicTable from "../../../components/Utils/DynamicTable/DynamicTable";
import Modal from "../../../components/Utils/Modal/Modal";
import Spinner from "../../../components/Utils/Spinner/Spinner";
import { StyledInputText } from "../../../components/Utils/StyledInput/StyledInput";
import useLoadingStore from "../../../stores/LoadingStore";
import {
  CategoryType,
  isCategoryType,
  SubCategoryType,
} from "../../../types/Category";
import appFetch from "../../../Utils/Services/Fetch/appFetch";
import ModalContent, { ModalFooterButton } from "./ModalContent/ModalContent";

const Category = () => {
  const [categories, setCategories] = useState<CategoryType[]>();
  const [subCategories, setSubCategories] = useState<SubCategoryType[]>();
  const [categoryType, setCategoryType] = useState({
    id: "category",
    name: "Catégorie",
  });
  const [search, setSearch] = useState("");
  const { loadingStates, setLoading } = useLoadingStore();
  const [updatedCategory, setUpdatedCategory] = useState<
    CategoryType | SubCategoryType
  >({
    category_id: -1,
    sub_category_id: -1,
    name: "",
    description: "",
  });

  const fetchData = async () => {
    setLoading("fetchCategories", true);
    const categoryData = await appFetch(`/api/category/getCategoryList`);
    if (categoryData) {
      setCategories(categoryData.categorys);
      setSubCategories(categoryData.subCategories);
    }
    setLoading("fetchCategories", false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const createCategory = async () => {
    if (!updatedCategory.name) {
      toast.error("Veuillez saisir le nom de la catégorie.");
      return;
    }
    setLoading("createCategory", true);
    const response = await appFetch(`/api/category/createCategory`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...updatedCategory,
        type: categoryType.id,
      }),
    });

    if (response && response.error) {
      toast.error(response.message);
    } else if (response && response.message) {
      document.getElementById("cancelModalButton")?.click();
      toast.success(response.message);
      fetchData();
      setUpdatedCategory({
        category_id: -1,
        name: "",
        description: "",
      });
    }
    setLoading("createCategory", false);
  };

  const updateCategory = async (category: CategoryType | SubCategoryType) => {
    const { name, description } = updatedCategory;
    const categoryId = isCategoryType(category)
      ? category.category_id
      : category.sub_category_id;
    if (!categoryId || categoryId === -1) {
      toast.error("ID de l'utilisateur non spécifié.");
      return;
    }
    setLoading("createCategory", true);

    const response = await appFetch(`/api/category/updateCategory`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        category_id: categoryId,
        name,
        description,
        type: categoryType.id,
      }),
    });

    if (response && response.error) {
      toast.error(response.message);
    } else if (response && response.message) {
      document.getElementById("cancelModalButton")?.click();
      toast.success(response.message);
      fetchData();
    }
    setLoading("createCategory", false);
  };

  const filteredCategories = (
    categoryType.id === "category" ? categories : subCategories
  )?.filter((category) => {
    return (
      category.name.toLowerCase().includes(search.toLowerCase()) ||
      category.description?.toLowerCase().includes(search.toLowerCase())
    );
  });

  const deleteCategory = async (category: CategoryType | SubCategoryType) => {
    const categoryId = isCategoryType(category)
      ? category.category_id
      : category.sub_category_id;
    if (!categoryId) return;

    setLoading(`delete${categoryId}`, true);
    const response = await appFetch(
      `/api/category/deleteCategory?category_id=${categoryId}&type=${categoryType.id}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (response && response.error) {
      toast.error(response.message);
    } else if (response && response.message) {
      document.getElementById("cancelModalButton")?.click();
      toast.success(response.message);

      // here we remove the deleted category from the list
      if (categoryType.id === "category") {
        setCategories(
          categories?.filter((category) => category.category_id !== categoryId),
        );
      } else {
        setSubCategories(
          subCategories?.filter(
            (subCategory) => subCategory.sub_category_id !== categoryId,
          ),
        );
      }
    }
    setLoading(`delete${categoryId}`, false);
  };

  const collumns = [
    {
      title: "Nom",
      dataIndex: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_: string, category: CategoryType | SubCategoryType) => (
        <div style={{ display: "flex", gap: "10px" }}>
          <Modal
            modalTitle={`Modifier la ${categoryType.name}`}
            modalContent={
              <ModalContent
                setupdatedCategory={setUpdatedCategory}
                updatedCategory={updatedCategory}
              />
            }
            modalButton={({ onClick }) => (
              <Button
                icon={<FiEdit2 />}
                onClick={onClick}
                className="editButton"
                style="square"
              />
            )}
            modalFooterButton={
              <ModalFooterButton
                onClick={() => updateCategory(category)}
                type="Modifier"
              />
            }
            onClick={() => setUpdatedCategory(category)}
          />

          <DeleteButton
            onClick={() => deleteCategory(category)}
            isLoading={
              loadingStates[
                `delete${isCategoryType(category) ? category.category_id : category.sub_category_id}`
              ]
            }
          />
        </div>
      ),
    },
  ];

  return (
    <div className={`categoryListPage`}>
      <div className="pageHeader">
        <StyledInputText
          label="Rechercher"
          value={search}
          setValue={setSearch}
          className="searchInput"
        />
        <ButtonSwitcher
          datas={[
            { id: "category", name: "Catégorie" },
            { id: "sub_category", name: "Sous-Catégorie" },
          ]}
          setValue={setCategoryType}
          value={categoryType}
          className="accountingFormButtonSwitch"
        />
        <Modal
          modalTitle={`Créer une ${categoryType.name}`}
          modalContent={
            <ModalContent
              setupdatedCategory={setUpdatedCategory}
              updatedCategory={updatedCategory}
            />
          }
          modalButton={({ onClick }) => (
            <Button label="Créer" onClick={onClick} className="createButton" />
          )}
          modalFooterButton={
            <ModalFooterButton onClick={createCategory} type="Créer" />
          }
          onClick={() => {
            setUpdatedCategory({
              category_id: -1,
              name: "",
              description: "",
            });
          }}
        />
      </div>

      <div className="tableContainer">
        {loadingStates.fetchCategories ? (
          <Spinner />
        ) : (
          <DynamicTable data={filteredCategories ?? []} columns={collumns} />
        )}
      </div>
    </div>
  );
};

export default Category;
